import { Text, Color, Badge, Font } from 'zens-storybook'
import { observer } from 'mobx-react-lite'
import { useTranslation, Trans } from 'react-i18next'
import { ListRenderItemInfo, StyleSheet, FlatList, View } from 'react-native'
import {
  TestState,
  titleForBatteryState,
  titleForDiagnostic,
  useStore,
  TestConnection,
  titleForProductConnectivityState,
  titleForProductPeripheralState,
  ProductPeripheralsState,
  ProductConnectivityState,
} from 'zens-store'
import { ReactNode } from 'react'

import ListItem from './listitem'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    flex: 1,
  },
  light: {
    color: Color.simplyWhite,
  },
  warning: {
    color: Color.collCopper,
    fontFamily: Font.medium,
  },
  error: {
    color: Color.soothingPink,
    fontFamily: Font.medium,
  },
})

interface Step {
  key: string
  title: string
  type: 'pass' | 'fail' | 'warning'
  details?: string | ReactNode
}

const TestResult = () => {
  const { test } = useStore()
  const { t } = useTranslation()

  const steps: Step[] = []
  if (test.hasActivity(TestState.STARTING))
    steps.push({
      key: 'T1',
      title: t('Charging'),
      type: test.isCharging ? 'pass' : 'fail',
      details: `${t('Battery state')}: ${titleForBatteryState(test.battery, t)}`,
    })
  if (test.hasActivity(TestState.RANGING))
    steps.push({
      key: 'T2',
      title: t('Bluetooth Beacon'),
      type: test.hasFoundBeacon ? 'pass' : 'fail',
      details: `UUID: ${test.beacon?.uuid}\nMajor: ${test.beacon?.major}\nMinor: ${test.beacon?.minor}\nRSSI: ${test.beacon?.rssi}`,
    })

  if (test.hasActivity(TestState.SCANNING))
    steps.push({
      key: 'T3',
      title: t('Bluetooth Connection'),
      type: test.isConnected ? 'pass' : 'fail',
      details: `${t('Manufacturer')}: ${test.manufacturer}\n${t('Serial number')}: ${test.serialNumber}\n${t(
        'Version',
      )}: ${test.modelVersion}`,
    })
  if (test.hasActivity(TestState.READING))
    steps.push({
      key: 'T4',
      title: t('Bluetooth Settings'),
      type: test.hasExpectedBeacon ? 'pass' : 'fail',
      details: `Major: ${test.beacon?.major} (${test.beacon?.major
        .toString(16)
        .padStart(4, '0')
        .toUpperCase()})\nMinor: ${test.beacon?.minor} (${test.beacon?.minor
        .toString(16)
        .padStart(4, '0')
        .toUpperCase()})`,
    })
  if (test.hasActivity(TestState.READING))
    steps.push({
      key: 'T5',
      title: t('Bluetooth identification'),
      type: test.hasExpectedBeacon ? 'pass' : 'fail',
      details: `${t('Name')}: ${test.device?.name}`,
    })
  if (test.hasActivity(TestState.READING)) {
    const { connectivityState, peripheralsState } = test.diagnosticResult || {}

    const isAttached = test.isAttached || test.diagnosticResult?.isAttached
    const isSensorConnected = test.isSensorConnected || test.diagnosticResult?.isSensorConnected
    const isLedConnected = test.isLedConnected || test.diagnosticResult?.isLedConnected
    steps.push({
      key: 'T6',
      title: t('Network connection'),
      type: isAttached
        ? !test.isAttached || (connectivityState !== undefined && connectivityState !== ProductConnectivityState.Connected)
          ? 'warning'
          : 'pass'
        : 'fail',
      details: (
        <Text style={styles.light}>
          <Trans>Network connection</Trans>
          {': '}
          <Text style={test.isAttached ? styles.light : styles.warning}>
            {test.isAttached ? 'Connected' : 'Not connected'}
          </Text>
          {'\n'}
          <Trans>Diagnostic</Trans>
          {': '}
          {titleForDiagnostic(test.diagnosticState, t)}
          {'\n'}
          <Trans>Message</Trans>
          {': '}
          {test.diagnosticMessage}
          {'\n'}
          {connectivityState !== undefined && (
            <Text style={styles.light}>
              <Trans>API Connectivity</Trans>
              {': '}
              <Text style={connectivityState === ProductConnectivityState.Connected ? styles.light : styles.warning}>
                {titleForProductConnectivityState(connectivityState, t)}
              </Text>
              {'\n'}
            </Text>
          )}
          <Trans>Voltage level</Trans>
          {': '}
          {test.voltageLevel || '-'}
          {'\n'}
          <Trans>Signal quality</Trans>
          {': '}
          {test.signalQuality || '-'}
        </Text>
      ),
    })
    steps.push({
      key: 'T7',
      title: t('Peripherals'),
      type:
        isSensorConnected === false || isLedConnected === false
          ? 'fail'
          : !isSensorConnected ||
              !isLedConnected ||
              test.signalQuality < 10
            ? 'warning'
            : 'pass',
      details: (
        <Text style={styles.light}>
          <Trans>Sensor</Trans>
          {`: `}
          {test.isSensorConnected === undefined ? (
            <Text style={styles.warning}>
              <Trans>Connection could not be verified</Trans>
            </Text>
          ) : test.isSensorConnected === false ? (
            <Text style={styles.error}>
              <Trans>Sensor disconnected</Trans>
            </Text>
          ) : test.sensorConnection !== TestConnection.OCCUPIED ? (
            <Trans>Not occupied</Trans>
          ) : (
            <Trans>Occupied</Trans>
          )}
          {'\n'}
          <Trans>Led</Trans>
          {`: `}
          {test.isLedConnected === undefined ? (
            <Text style={styles.warning}>
              <Trans>Connection could not be verified</Trans>
            </Text>
          ) : test.isLedConnected === false ? (
            <Text style={styles.error}>
              <Trans>Led disconnected</Trans>
            </Text>
          ) : (
            <Trans>Connected</Trans>
          )}
          {peripheralsState !== undefined ? (
            <>
              {`\n${t('API Peripherals status')}: `}
              <Text
                style={
                  peripheralsState === ProductPeripheralsState.Unknown
                    ? styles.warning
                    : peripheralsState === ProductPeripheralsState.SensorDisconnected ||
                        peripheralsState === ProductPeripheralsState.LedDisconnected ||
                        peripheralsState === ProductPeripheralsState.SensorAndLedDisconnected
                      ? styles.error
                      : styles.light
                }
              >
                {titleForProductPeripheralState(peripheralsState, t)}
              </Text>
            </>
          ) : (
            ''
          )}
          {test.signalQuality < 10 && (
            <>
              {'\n'}
              <Trans>Signal quality</Trans>
              {`: `}
              <Text style={styles.warning}>
                <Trans>Signal quality is low</Trans>
              </Text>
            </>
          )}
        </Text>
      ),
    })
  }

  const renderStep = (info: ListRenderItemInfo<Step>) => {
    const { item } = info
    return (
      <ListItem
        content={
          <View style={styles.content}>
            <Text level={5} style={[styles.title, styles.light]}>
              {item.title}
            </Text>
            <Badge
              light
              title={item.type === 'warning' ? t('Warning') : item.type === 'fail' ? t('Fail') : t('Pass')}
              status={item.type === 'warning' ? 'warning' : item.type === 'fail' ? 'error' : 'success'}
            />
          </View>
        }
        details={typeof item.details === 'string' ? <Text style={styles.light}>{item.details}</Text> : item.details}
      />
    )
  }

  return <FlatList style={styles.container} data={steps} renderItem={renderStep} ItemSeparatorComponent={() => null} />
}

export default observer(TestResult)
